'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';

export interface Company {
  id: string;
  ownerId: string;
  companyName: string;
  companyType: string;
  gstNumber: string;
  cinNumber: string;
  panNumber: string;
  msmeNumber: string;
  email: string;
  mobile: string;
  landline: string;
  website: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  invoicePrefix: string;
  nextInvoiceNumber: number;
  quotationPrefix: string;
  nextQuotationNumber: number;
  themeColor: string;
  logoUrl: string;
  useLetterhead: boolean;
  isActive: boolean;
  createdAt: string;
  // Bank fields
  bankName: string;
  bankBranch: string;
  accountHolder: string;
  accountNumber: string;
  ifscCode: string;
  upiId: string;
  qrCodeUrl: string;
  // Seal fields
  sealText: string;
  sealRegistrationText: string;
  sealCity: string;
  sealColor: string;
  sealStyle: string;
  sealSize: string;
  sealRotation: number;
  sealOpacity: number;
  sealOnInvoice: boolean;
  sealOnQuotation: boolean;
  // Signature fields
  signatureUrl: string;
  signatureOnInvoice: boolean;
  signatureOnQuotation: boolean;
  // Designation for signatory block
  designation: string;
  // Tax defaults
  defaultGstPercent: number;
  defaultCgstSgst: number;
  defaultIgst: number;
}

interface UserSubscription {
  maxCompanies: number;
  subscriptionPlan: string;
  subscriptionStatus: string;
  subscriptionExpiry: string;
}

interface CompanyContextType {
  companies: Company[];
  activeCompany: Company | null;
  loading: boolean;
  subscription: UserSubscription;
  setActiveCompany: (company: Company) => void;
  refreshCompanies: () => Promise<void>;
  createCompany: (data: Partial<Company>) => Promise<Company | null>;
  canCreateCompany: () => boolean;
}

const DEFAULT_SUBSCRIPTION: UserSubscription = {
  maxCompanies: 3,
  subscriptionPlan: 'Professional',
  subscriptionStatus: 'active',
  subscriptionExpiry: '',
};

const CompanyContext = createContext<CompanyContextType>({
  companies: [],
  activeCompany: null,
  loading: true,
  subscription: DEFAULT_SUBSCRIPTION,
  setActiveCompany: () => {},
  refreshCompanies: async () => {},
  createCompany: async () => null,
  canCreateCompany: () => true,
});

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
};

const ACTIVE_COMPANY_KEY = 'nova_active_company_id';

function mapRow(row: any): Company {
  return {
    id: row.id,
    ownerId: row.owner_id,
    companyName: row.company_name,
    companyType: row.company_type,
    gstNumber: row.gst_number || '',
    cinNumber: row.cin_number || '',
    panNumber: row.pan_number || '',
    msmeNumber: row.msme_number || '',
    email: row.email || '',
    mobile: row.mobile || '',
    landline: row.landline || '',
    website: row.website || '',
    address: row.address || '',
    city: row.city || '',
    state: row.state || '',
    country: row.country || 'India',
    pincode: row.pincode || '',
    invoicePrefix: row.invoice_prefix || 'INV',
    nextInvoiceNumber: row.next_invoice_number || 1,
    quotationPrefix: row.quotation_prefix || 'QT',
    nextQuotationNumber: row.next_quotation_number || 1,
    themeColor: row.theme_color || '#2563EB',
    logoUrl: row.logo_url || '',
    useLetterhead: row.use_letterhead || false,
    isActive: row.is_active !== false,
    createdAt: row.created_at,
    bankName: row.bank_name || '',
    bankBranch: row.bank_branch || '',
    accountHolder: row.account_holder || '',
    accountNumber: row.account_number || '',
    ifscCode: row.ifsc_code || '',
    upiId: row.upi_id || '',
    qrCodeUrl: row.qr_code_url || '',
    sealText: row.seal_text || '',
    sealRegistrationText: row.seal_registration_text || '',
    sealCity: row.seal_city || '',
    sealColor: row.seal_color || '#1e3a8a',
    sealStyle: row.seal_style || 'round',
    sealSize: row.seal_size || '100px',
    sealRotation: row.seal_rotation || 0,
    sealOpacity: row.seal_opacity || 1,
    sealOnInvoice: row.seal_on_invoice || false,
    sealOnQuotation: row.seal_on_quotation || false,
    signatureUrl: row.signature_url || '',
    signatureOnInvoice: row.signature_on_invoice || false,
    signatureOnQuotation: row.signature_on_quotation || false,
    designation: row.designation || 'Proprietor',
    defaultGstPercent: row.default_gst_percent ?? 18,
    defaultCgstSgst: row.default_cgst_sgst ?? 9,
    defaultIgst: row.default_igst ?? 18,
  };
}

export const CompanyProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<UserSubscription>(DEFAULT_SUBSCRIPTION);
  const supabase = createClient();

  const refreshCompanies = useCallback(async () => {
    if (!user) {
      setCompanies([]);
      setActiveCompanyState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Fetch companies and user subscription in parallel
      const [companiesResult, profileResult] = await Promise.all([
        supabase
          .from('companies')
          .select('*')
          .eq('owner_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: true }),
        supabase
          .from('user_profiles')
          .select('max_companies, subscription_plan, subscription_status, subscription_expiry')
          .eq('id', user.id)
          .single(),
      ]);

      if (companiesResult.error) {
        console.log('Companies fetch error:', companiesResult.error.message);
        setCompanies([]);
        setLoading(false);
        return;
      }

      const mapped = (companiesResult.data || []).map(mapRow);
      setCompanies(mapped);

      // Update subscription info
      if (!profileResult.error && profileResult.data) {
        const p = profileResult.data;
        setSubscription({
          maxCompanies: p.max_companies ?? 3,
          subscriptionPlan: p.subscription_plan || 'Professional',
          subscriptionStatus: p.subscription_status || 'active',
          subscriptionExpiry: p.subscription_expiry || '',
        });
      }

      // Restore last active company from localStorage
      const savedId = typeof window !== 'undefined' ? localStorage.getItem(ACTIVE_COMPANY_KEY) : null;
      const saved = savedId ? mapped.find((c) => c.id === savedId) : null;
      if (saved) {
        setActiveCompanyState(saved);
      } else if (mapped.length === 1) {
        setActiveCompanyState(mapped[0]);
        if (typeof window !== 'undefined') localStorage.setItem(ACTIVE_COMPANY_KEY, mapped[0].id);
      } else {
        setActiveCompanyState(null);
      }
    } catch (e) {
      console.log('Companies error:', e);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshCompanies();
  }, [refreshCompanies]);

  const setActiveCompany = useCallback((company: Company) => {
    setActiveCompanyState(company);
    if (typeof window !== 'undefined') {
      localStorage.setItem(ACTIVE_COMPANY_KEY, company.id);
    }
  }, []);

  const canCreateCompany = useCallback((): boolean => {
    if (subscription.maxCompanies === -1) return true; // Unlimited
    return companies.length < subscription.maxCompanies;
  }, [companies.length, subscription.maxCompanies]);

  const createCompany = useCallback(async (data: Partial<Company>): Promise<Company | null> => {
    if (!user) return null;
    try {
      const { data: row, error } = await supabase
        .from('companies')
        .insert({
          owner_id: user.id,
          company_name: data.companyName || '',
          company_type: data.companyType || 'OPC',
          gst_number: data.gstNumber || '',
          cin_number: data.cinNumber || '',
          pan_number: data.panNumber || '',
          email: data.email || '',
          mobile: data.mobile || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          pincode: data.pincode || '',
          invoice_prefix: data.invoicePrefix || 'INV',
          next_invoice_number: data.nextInvoiceNumber || 1,
          quotation_prefix: data.quotationPrefix || 'QT',
          next_quotation_number: data.nextQuotationNumber || 1,
          theme_color: data.themeColor || '#2563EB',
          logo_url: data.logoUrl || '',
          use_letterhead: data.useLetterhead || false,
          bank_name: data.bankName || '',
          bank_branch: data.bankBranch || '',
          account_holder: data.accountHolder || '',
          account_number: data.accountNumber || '',
          ifsc_code: data.ifscCode || '',
          upi_id: data.upiId || '',
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.log('Create company error:', error.message);
        return null;
      }
      const company = mapRow(row);
      await refreshCompanies();
      return company;
    } catch (e) {
      console.log('Create company error:', e);
      return null;
    }
  }, [user, refreshCompanies]);

  return (
    <CompanyContext.Provider value={{ companies, activeCompany, loading, subscription, setActiveCompany, refreshCompanies, createCompany, canCreateCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};
